import {gql} from '@apollo/client';
import {captureException} from '@sentry/browser';
import {css, StyleSheet} from 'aphrodite';
import React, {createContext, ErrorInfo, ReactNode, useMemo} from 'react';

import {AgentType, ChatType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {Chat} from 'llm/chat-library/Chat';
import {MCPToolMappingProvider} from 'llm/mcp-client-library/MCPToolMappingContext';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {assertNever} from 'util/Types';

import {ChatTypeQuery, ChatTypeQueryVariables} from './__generated/ChatTypeQuery';

/**
 * An existing chat that can be rendered.
 *
 * |  id    |   type   |  intention                                                                   |
 * | ------ | -------- | ---------------------------------------------------------------------------- |
 * | string |  null    | An existing chat whose type is not known. A query will resolve the type.     |
 * | string | ChatType | An existing chat whose type is known. Renders immediately.                   |
 */
export interface ExistingChat {
  id: string;
  type: ChatType | null;
}

/**
 * The "no active chat" state: the sidepanel is open but no chat is selected yet.
 *
 * The actual draft content (message text, files, agent type) is stored separately
 * via {@link getDraftState}(null) / {@link setDraftState}(null, ...).
 */
export interface DraftChat {
  id: null;
}

/**
 * The state of the active chat slot in the sidepanel.
 *
 * - {@link ExistingChat}: a persisted chat is being viewed.
 * - {@link DraftChat}: no chat is active (the welcome panel is shown).
 */
export type SidepanelChat = ExistingChat | DraftChat;

/**
 * Metadata from a newly created chat, passed to the onCreateChat callback.
 */
export interface CreateChatResult {
  chatSessionId: string;
  chatType: ChatType;
  initialAgentType: AgentType;
  /** When Voice is started from the welcome panel, keep the sidepanel compact instead of expanding. Text-created chats omit this. */
  keepCompact?: boolean;
}

export const CHAT_TYPE_QUERY = gql`
  query ChatTypeQuery($id: StrictString!) @engComponent(name: LLM) @skipAudit {
    chatSession(id: $id) {
      ... on ChatSessionNode {
        id
        type
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/**
 * Given an {@link ExistingChat}, determine the chat type, querying the backend if necessary.
 */
function useResolvedType(
  chat: ExistingChat
): {status: 'RESOLVED'; value: ChatType | null} | {status: 'INACCESSIBLE' | 'PENDING' | 'ERROR'} {
  const result = useSkippableBenchlingQuery<ChatTypeQuery, ChatTypeQueryVariables>(CHAT_TYPE_QUERY, {
    variables: {id: chat.id},
    fetchPolicy: 'cache-first',
    skip: chat.type != null,
  });
  if (chat.type != null) {
    return {status: 'RESOLVED', value: chat.type};
  }
  switch (result.type) {
    case QueryResultType.SUCCESS:
      return !isInaccessible(result.data.chatSession)
        ? {status: 'RESOLVED', value: result.data.chatSession.type}
        : {status: 'INACCESSIBLE'};
    case QueryResultType.ERROR:
      return {status: 'ERROR'};
    case QueryResultType.PENDING:
      return {status: 'PENDING'};
    case QueryResultType.SKIPPED:
      throw new Error(`Unexpected query status: ${result.type}`);
    default:
      throw new Error(`Unexpected query status: ${assertNever(result).type}`);
  }
}

/**
 * Given a chat whose type is known, route to the appropriate chat component.
 */
function ResolvedTypeChat({
  chat,
  isExpanded,
}: {
  chat: ExistingChat & {type: ChatType};
  isExpanded: boolean;
}): JSX.Element {
  const contextValue = useMemo(() => ({chatId: chat.id, chatType: chat.type}), [chat.id, chat.type]);

  return (
    <MCPToolMappingProvider>
      <ChatContext.Provider value={contextValue}>
        <Chat chatSessionId={chat.id} chatType={chat.type} isExpanded={isExpanded} />
      </ChatContext.Provider>
    </MCPToolMappingProvider>
  );
}

/**
 * A chat container that takes up the full width of the expanded sidepanel with a reasonable max width.
 *
 * TODO: This should be pulled out into a shared component.
 */
function FullWidthChatContainer({children}: {children?: ReactNode}): JSX.Element {
  return <div className={css(styles.fullWidthChatContainer)}>{children}</div>;
}

/**
 * Renders an error message in place of the chat component
 */
function ErrorChat({message}: {message: string}): JSX.Element {
  return (
    <FullWidthChatContainer>
      <InlineBanner text={message} bannerType={InlineBannerType.ERROR} />
    </FullWidthChatContainer>
  );
}

/**
 * Ideally, this would be a new chat where the user hasn't specified the type yet. We don't support
 * this yet, so for now, we render a blank page. The user can still interact with the rest of the sidepanel.
 */
function EmptyNewChat(): JSX.Element {
  return <FullWidthChatContainer />;
}

/**
 * Render a loading indicator in place of the chat component.
 */
function LoadingChat(): JSX.Element {
  return (
    <div className={css(styles.loadingContainer)}>
      <CenteredHorizontalThrobber />
    </div>
  );
}

interface ChatRouterProps {
  chat: ExistingChat;
  isExpanded: boolean;
}

interface ChatRouterState {
  hasError: boolean;
}

export const ChatContext = createContext<{chatId: string | null; chatType: ChatType} | null>(null);

/**
 * Main rendering for {@link ChatRouter}, factored out into its own component to add an error boundary.
 */
function InnerChatRouter({chat, isExpanded}: ChatRouterProps): JSX.Element | null {
  const result = useResolvedType(chat);
  switch (result.status) {
    case 'RESOLVED':
      return result.value != null ? (
        <ResolvedTypeChat chat={{id: chat.id, type: result.value}} isExpanded={isExpanded} />
      ) : (
        <EmptyNewChat />
      );
    case 'INACCESSIBLE':
      return <ErrorChat message="You do not have access to this chat." />;
    case 'PENDING':
      return <LoadingChat />;
    case 'ERROR':
      return <ErrorChat message="An error occurred loading the chat." />;
    default:
      throw new Error(`Unexpected result: ${assertNever(result).type}`);
  }
}

/**
 * Routes an {@link ExistingChat} to the appropriate chat component.
 * Includes an error boundary to render an inline error and report to Sentry.
 *
 * In the case one chat is being replaced by a different chat, it is recommended that the ChatRouter
 * receive a new key, forcing it to unmount and re-mount.
 */
export default class ChatRouter extends React.Component<ChatRouterProps, ChatRouterState> {
  override state: ChatRouterState = {hasError: false};

  static getDerivedStateFromError(error: Error): ChatRouterState {
    return {hasError: true};
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    captureException(error, {extra: {errorInfo}});
  }

  override render(): ReactNode {
    return this.state.hasError ? (
      <ErrorChat message="An error occurred displaying the chat. Try refreshing the page." />
    ) : (
      <InnerChatRouter {...this.props} />
    );
  }
}

const styles = StyleSheet.create({
  fullWidthChatContainer: {
    width: '100%',
    margin: '0 auto',
    maxWidth: '800px',
    padding: '16px',
  },
  loadingContainer: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  },
});
